/* eslint-disable react/prop-types */
import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  SectionHeader,
  MapDisplay,
  LoadingContainer,
  Modal,
  SegmentedControl,
  ExpandableItem,
  ArtsDisciplinesTabContent,
  ProgramsTabContent,
  OutcomeTypesTabContent,
  ApproachesTabContent,
  LanguagesTabContent,
  ProgramBlock,
  ImageCarousel,
  SpotlightVideoPlayer,
} from 'components'
import * as Types from 'types'
import { Spinner, TabBar } from 'lp-components'
import { isEmpty, flatMap, chunk } from 'lodash'
import {
  BuildingIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  BookIcon,
  MortarboardIcon,
  CertificationIcon,
  ProgramsIcon,
  VectorIcon,
} from 'components/icons'
import imagePlaceholder from 'images/image-placeholder.jpeg'
import { Link } from 'react-router'
import {
  formatMonthYearDate,
  getTotalExperience,
  formatDate,
} from 'utils/local'
import { PARTNER_ROUTE, SCHOOL_ROUTE } from 'config'
import scrollToHash from 'utils/local/scroll-to-hash'
import { useCommunity } from 'utils'

const propTypes = {
  isLoading: PropTypes.bool.isRequired,
  artist: Types.artist,
  artistLocation: Types.location,
  organizationLocations: PropTypes.arrayOf(Types.location),
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  programTypes: PropTypes.arrayOf(Types.programType).isRequired,
  outcomeTypes: PropTypes.arrayOf(Types.outcomeType).isRequired,
  approaches: PropTypes.arrayOf(Types.approach).isRequired,
  languages: PropTypes.arrayOf(Types.language).isRequired,
}

const defaultProps = {
  artist: null,
}

function ArtistContent({
  isLoading,
  organizationLocations,
  artist,
  artistLocation,
  disciplines,
  programTypes,
  outcomeTypes,
  approaches,
  languages,
}) {
  const community = useCommunity()
  if (!artist) return <Spinner />

  const spotlightTranscriptRequestEmail =
    community.artistSupportEmail ||
    community.partnerSupportEmail ||
    'info@artlookmap.com'

  const SkillsTabs = {
    APPROACHES: 'approaches',
    ARTS_DISCIPLINES: 'disciplines',
    OUTCOME_TYPES: 'outcomes',
    PROGRAMS: 'programs',
    LANGUAGES: 'languages',
  }

  // Skills tab option constants for better maintainability
  const SKILLS_TAB_OPTIONS = {
    APPROACHES: {
      key: 'Approaches',
      value: SkillsTabs.APPROACHES,
    },
    ARTS_DISCIPLINES: {
      key: 'Arts Disciplines',
      value: SkillsTabs.ARTS_DISCIPLINES,
    },
    OUTCOME_TYPES: {
      key: 'Teaching Qualities and Intended Outcomes',
      value: SkillsTabs.OUTCOME_TYPES,
    },
    PROGRAMS: {
      key: 'Types of Programs',
      value: SkillsTabs.PROGRAMS,
    },
    LANGUAGES: {
      key: 'Languages',
      value: SkillsTabs.LANGUAGES,
    },
  }

  const [mapShown, setMapShown] = useState(false)
  const [skillsTab, setSkillsTab] = useState(SkillsTabs.APPROACHES)
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [showTranscriptModal, setShowTranscriptModal] = useState(false)

  const { partnerships, credentials, experiences, skills, taPrograms } = artist

  const combinedSkills = useMemo(() => {
    return flatMap(skills, (values) => values)
  }, [skills])

  // Handle hash scrolling when component mounts
  useEffect(() => {
    const location = window.location
    scrollToHash({ location })
  }, [])

  const organizations = partnerships.filter((p) => !!p.organizationId)
  const schools = partnerships.filter((p) => !!p.schoolId)
  const educations = credentials.filter(
    (c) => c.credentialType === Types.CREDENTIAL_TYPES.DEGREE
  )
  const certifications = credentials.filter(
    (c) => c.credentialType === Types.CREDENTIAL_TYPES.LICENSE
  )
  const professionalLearning = credentials.filter(
    (c) => c.credentialType === Types.CREDENTIAL_TYPES.PROFESSIONAL_LEARNING
  )

  const hasOrganizations = !isEmpty(organizations)
  const hasSchools = !isEmpty(schools)
  const hasEducations = !isEmpty(educations)
  const hasExperiences = !isEmpty(experiences)
  const hasSkills = !isEmpty(combinedSkills)
  const hasPrograms = !isEmpty(taPrograms)
  const hasCertifications = !isEmpty(certifications)
  const hasProfessionalLearning = !isEmpty(professionalLearning)
  const hasSpotlightVideo =
    !!artist.spotlightVideoUrl && !!community?.teachingArtist?.photoVideoEnabled
  const spotlightTranscriptText = (artist.spotlightVideoTranscript || '').trim()
  const spotlightCaptionsUrl = artist.spotlightVideoCaptionsUrl || ''
  const spotlightCaptionWords = artist.spotlightVideoCaptionWords
  const spotlightMediaAltText = (
    artist.spotlightVideoMediaAlternative || ''
  ).trim()
  const spotlightMediaAltId = `spotlight-video-media-alt-${artist.id}`
  const hasSpotlightMediaAlternative = spotlightMediaAltText.length > 0
  const hasSpotlightTranscript = spotlightTranscriptText.length > 0
  const hasSpotlightImages =
    !isEmpty(artist.spotlightImages) &&
    !!community?.teachingArtist?.photoVideoEnabled
  const programPhotoEnabled = !!community?.teachingArtist?.programPhotoEnabled

  const programGroups = chunk(taPrograms, 2)

  const hasContent =
    hasOrganizations ||
    hasSchools ||
    hasEducations ||
    hasExperiences ||
    hasSkills ||
    hasPrograms ||
    hasCertifications ||
    hasProfessionalLearning ||
    hasSpotlightVideo ||
    hasSpotlightImages

  if (!hasContent) return null

  function OrganizationHeader({ organization }) {
    return (
      <React.Fragment>
        <div className="image-block" aria-hidden>
          <div className="image-thumbnail">
            <img
              src={organization.organizationLogo?.src || imagePlaceholder}
              alt=""
            />
          </div>
        </div>
        <div className="section-header-details">
          <Link
            to={`${PARTNER_ROUTE}/${organization.organizationId}`}
            className="section-header-title"
          >
            {organization.organizationName}
          </Link>
          {organization.state === 'pending_approval' ? (
            <span className="pending-approval">Pending Approval</span>
          ) : null}
        </div>
        {organization?.partnerPrograms?.length > 0 && (
          <>
            <div className="arrow-icon-container arrow-icon-up">
              <ArrowUpIcon />
            </div>
            <div className="arrow-icon-container arrow-icon-down">
              <ArrowDownIcon />
            </div>
          </>
        )}
      </React.Fragment>
    )
  }

  return (
    <LoadingContainer isLoading={isLoading}>
      <nav></nav>

      {/* Spotlight Video */}
      {hasSpotlightVideo && (
        <section name="spotlight-video">
          <div className="content-block-container spotlight-video-section">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="38"
                    height="33"
                    viewBox="0 0 38 33"
                    fill="none"
                  >
                    <path
                      d="M1.5 6.72448V26.2821C1.5 30.2877 5.85293 32.8013 9.32712 30.7986L17.8082 25.9142L26.2894 21.0095C29.7636 19.0067 29.7636 13.9998 26.2894 11.997L17.8082 7.09232L9.32712 2.20805C5.85293 0.205281 1.5 2.69851 1.5 6.72448Z"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M36.5 28.7613V3.50195"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <h2>Spotlight Video</h2>
            </SectionHeader>
            <div className="row spotlight-video-content">
              <div className="col-6 spotlight-video-text">
                <h3>Welcome To {artist.name}</h3>
                {artist.spotlightVideoCaption && (
                  <p>{artist.spotlightVideoCaption}</p>
                )}
              </div>
              <div className="col-6 spotlight-video-thumbnail-col">
                <button
                  className="spotlight-video-thumbnail-btn"
                  onClick={() => setShowVideoModal(true)}
                  aria-label={`Play spotlight video for ${artist.name}`}
                >
                  <span className="spotlight-video-thumbnail-wrap">
                    <img
                      src={artist.spotlightVideoUrl
                        .replace('/video/upload/', '/video/upload/so_0/')
                        .replace(/\.mp4(\?.*)?$/, '.jpg')}
                      alt={`${artist.name} spotlight video thumbnail`}
                      className="spotlight-video-thumbnail"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                    <span
                      className="spotlight-video-thumbnail-play"
                      aria-hidden="true"
                    >
                      <span className="spotlight-video-thumbnail-play-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="28"
                          viewBox="0 0 29 40"
                          fill="none"
                        >
                          <path
                            d="M0.5 36.8261V2.93137C0.5 0.946106 2.77372 -0.216001 4.37016 0.994527L26.9622 17.9419C28.2684 18.9103 28.2684 20.8472 26.9622 21.8156L4.37016 38.763C2.77372 39.9735 0.5 38.8114 0.5 36.8261Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
          {showVideoModal && (
            <Modal
              onClose={() => {
                setShowTranscriptModal(false)
                setShowVideoModal(false)
              }}
              className="spotlight-video-modal"
            >
              <SpotlightVideoPlayer
                videoSrc={artist.spotlightVideoUrl}
                captionsUrl={spotlightCaptionsUrl}
                captionWords={spotlightCaptionWords}
                videoAriaLabel={`Spotlight video for ${artist.name}`}
                ariaDescribedBy={
                  hasSpotlightMediaAlternative ? spotlightMediaAltId : undefined
                }
                videoStyle={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  display: 'block',
                }}
              />
              {hasSpotlightMediaAlternative ? (
                <section
                  id={spotlightMediaAltId}
                  className="spotlight-video-media-alternative"
                  tabIndex={-1}
                >
                  <h3 className="spotlight-video-media-alternative-heading">
                    Text version of this video
                  </h3>
                  <div
                    className="spotlight-video-media-alternative-body"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {spotlightMediaAltText}
                  </div>
                </section>
              ) : null}
              <div className="spotlight-video-transcript">
                {hasSpotlightTranscript ? (
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => setShowTranscriptModal(true)}
                    aria-label={`View transcript for ${artist.name} spotlight video`}
                  >
                    View Transcript
                  </button>
                ) : (
                  <div
                    className="spotlight-video-equivalent-info"
                    role="region"
                    aria-label="Video description and transcript information"
                  >
                    {artist.spotlightVideoCaption && (
                      <React.Fragment>
                        <h3 className="spotlight-video-equivalent-heading">
                          About this video
                        </h3>
                        <p>{artist.spotlightVideoCaption}</p>
                      </React.Fragment>
                    )}
                    <p>
                      A full text transcript is not published with this
                      spotlight video.
                    </p>
                    <p>
                      For a transcript or a text description of the audio, email{' '}
                      <a href={`mailto:${spotlightTranscriptRequestEmail}`}>
                        {spotlightTranscriptRequestEmail}
                      </a>
                      .
                    </p>
                  </div>
                )}
              </div>
            </Modal>
          )}
          {showTranscriptModal && hasSpotlightTranscript && (
            <Modal
              onClose={() => setShowTranscriptModal(false)}
              className="spotlight-transcript-modal"
            >
              <h2 className="spotlight-transcript-modal-title">Transcript</h2>
              <div className="spotlight-transcript-modal-body">
                {spotlightTranscriptText}
              </div>
            </Modal>
          )}
        </section>
      )}

      {/* Spotlight Images */}
      {hasSpotlightImages && (
        <section name="spotlight-images">
          <div className="content-block-container spotlight-images-section">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </div>
              </div>
              <h2>Spotlight Images</h2>
            </SectionHeader>
            <ImageCarousel
              autoPlay
              images={artist.spotlightImages}
              imagesPerPage={4}
              modalImagesPerPage={2}
            />
          </div>
        </section>
      )}

      {/* Organizations */}
      {hasOrganizations && (
        <section name="organizations">
          <div className="content-block-container">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <BuildingIcon />
                </div>
                <span>{organizations.length}</span>
              </div>
              <h2>Associated Organizations & Programs</h2>
              <SegmentedControl
                options={['List', 'Map']}
                value={mapShown ? 'Map' : 'List'}
                onChange={(val) => setMapShown(val === 'Map')}
              />
              <div className="header-title">Organization Name</div>
            </SectionHeader>
            {mapShown ? (
              <MapDisplay
                locations={[...organizationLocations, artistLocation]}
                autoZoom
              />
            ) : (
              <div className="cards-container grid-container col-2">
                {organizations.map((organization) => (
                  <div
                    className="card organization-card"
                    key={`organization-${organization.organizationId}`}
                  >
                    <ExpandableItem
                      disabled={organization?.partnerPrograms?.length === 0}
                      headerComponent={OrganizationHeader}
                      organization={organization}
                    >
                      <ul className="program-list">
                        {organization?.partnerPrograms?.map((program) => (
                          <li
                            key={`organization-${organization.organizationId}-program-${program.id}`}
                          >
                            <Link
                              to={`${PARTNER_ROUTE}/${organization.organizationId}#partner-programs`}
                            >
                              {program.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </ExpandableItem>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Schools */}
      {hasSchools && (
        <section name="schools">
          <div className="content-block-container">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <BookIcon />
                </div>
                <span>{schools.length}</span>
              </div>
              <h2>Associated Schools</h2>
              <div className="header-title">School Name</div>
            </SectionHeader>
            <ul className="school-list">
              {schools.map((school) => (
                <li key={`school-${school.schoolId}`}>
                  {community?.schoolDisabled ? (
                    school.schoolName
                  ) : (
                    <Link to={`${SCHOOL_ROUTE}/${school.schoolId}`}>
                      {school.schoolName}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Education */}
      {hasEducations && (
        <section name="educations">
          <div className="content-block-container">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <MortarboardIcon />
                </div>
                <span>{educations.length}</span>
              </div>
              <h2>Education</h2>
            </SectionHeader>
            <ul className="grid-container">
              {educations.map((education) => (
                <li
                  key={`education-${education.id}`}
                  className="card flex-container"
                >
                  <div className="grid-container">
                    {education.institutionName && (
                      <p className="education-institution">
                        {education.institutionName}
                      </p>
                    )}
                    {education.credentialName && (
                      <p className="education-degree">
                        {education.credentialName}
                        {education.fieldOfStudyCourseName &&
                          ', ' + education.fieldOfStudyCourseName}
                      </p>
                    )}
                    {education.issueDate && (
                      <p className="education-date">
                        {formatMonthYearDate(education.issueDate)}
                      </p>
                    )}
                    {education.description && (
                      <p className="education-description">
                        {education.description}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Experiences */}
      {hasExperiences && (
        <section name="experiences">
          <div className="content-block-container">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <MortarboardIcon />
                </div>
                <span>{experiences.length}</span>
              </div>
              <h2>Experience</h2>
            </SectionHeader>
            <ul className="grid-container">
              {experiences.map((experience) => (
                <li
                  key={`experience-${experience.id}`}
                  className="card flex-container"
                >
                  <div className="grid-container">
                    {experience.organizationName && (
                      <p className="experience-company">
                        {experience.organizationName}
                      </p>
                    )}
                    {experience && (
                      <p className="experience-date">
                        {getTotalExperience([experience]).displayText}
                      </p>
                    )}
                    {experience.location && (
                      <p className="experience-location">
                        [ experience.locationCity, experience.locationState,
                        experience.locationCountry ].filter(Boolean).join(', ')
                      </p>
                    )}
                    {/* {experience.positions.map((position) => ( */}
                    <div className="experience-position">
                      {experience.title && (
                        <p className="experience-title">{experience.title}</p>
                      )}
                      {experience.startDate && (
                        <p className="experience-date">
                          {formatMonthYearDate(experience.startDate)} -{' '}
                          {experience.endDate
                            ? formatMonthYearDate(experience.endDate)
                            : 'Present'}{' '}
                          • {getTotalExperience([experience]).displayText}
                        </p>
                      )}
                      {experience.description && (
                        <p className="experience-description">
                          {experience.description}
                        </p>
                      )}
                    </div>
                    {/* ))} */}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Skills */}
      {hasSkills && (
        <section name="skills" className="skills-wrapper">
          <div className="content-block-container">
            <SectionHeader>
              <div className="image-block">
                <ProgramsIcon />
              </div>
              <h2>Skills and Offerings</h2>
              <div className="text-block">
                <p>
                  This Teaching Artist offers a range of skills and services.
                  The checked items below highlight the specific areas of
                  expertise and offerings available.
                </p>
              </div>
            </SectionHeader>

            <TabBar
              options={[
                ...(!isEmpty(approaches)
                  ? [SKILLS_TAB_OPTIONS.APPROACHES]
                  : []),
                ...(!isEmpty(disciplines)
                  ? [SKILLS_TAB_OPTIONS.ARTS_DISCIPLINES]
                  : []),
                ...(!isEmpty(outcomeTypes)
                  ? [SKILLS_TAB_OPTIONS.OUTCOME_TYPES]
                  : []),
                ...(!isEmpty(programTypes)
                  ? [SKILLS_TAB_OPTIONS.PROGRAMS]
                  : []),
                ...(!isEmpty(languages) ? [SKILLS_TAB_OPTIONS.LANGUAGES] : []),
              ]}
              value={skillsTab}
              onChange={setSkillsTab}
              className="accordion-tabs-minimal"
              activeClassName="is-active"
            />
            {skillsTab === SkillsTabs.APPROACHES && !isEmpty(approaches) && (
              <div className="tab-content tab-content-1">
                <ApproachesTabContent
                  approachesResources={skills.approaches}
                  allApproachesTypes={approaches}
                  isActive={skillsTab === SkillsTabs.APPROACHES}
                  setActive={() => setSkillsTab(SkillsTabs.APPROACHES)}
                />
              </div>
            )}
            {skillsTab === SkillsTabs.ARTS_DISCIPLINES &&
              !isEmpty(disciplines) && (
                <div className="tab-content tab-content-2">
                  <ArtsDisciplinesTabContent
                    artsDisciplines={skills.subDisciplines}
                    disciplines={disciplines}
                    isActive={skillsTab === SkillsTabs.ARTS_DISCIPLINES}
                    setActive={() => setSkillsTab(SkillsTabs.ARTS_DISCIPLINES)}
                  />
                </div>
              )}
            {skillsTab === SkillsTabs.OUTCOME_TYPES &&
              !isEmpty(outcomeTypes) && (
                <div className="tab-content tab-content-3">
                  <OutcomeTypesTabContent
                    requestedOutcomeTypes={skills.outcomes}
                    outcomeTypes={outcomeTypes}
                    isActive={skillsTab === SkillsTabs.OUTCOME_TYPES}
                    setActive={() => setSkillsTab(SkillsTabs.OUTCOME_TYPES)}
                  />
                </div>
              )}
            {skillsTab === SkillsTabs.PROGRAMS && !isEmpty(programTypes) && (
              <div className="tab-content tab-content-4">
                <ProgramsTabContent
                  allProgramTypes={programTypes}
                  artsProgrammingResources={skills.programTypes}
                  isActive={skillsTab === SkillsTabs.PROGRAMS}
                  setActive={() => setSkillsTab(SkillsTabs.PROGRAMS)}
                />
              </div>
            )}
            {skillsTab === SkillsTabs.LANGUAGES && !isEmpty(languages) && (
              <div className="tab-content tab-content-5">
                <LanguagesTabContent
                  artsLanguagesResources={skills.languages}
                  allLanguages={languages}
                  isActive={skillsTab === SkillsTabs.LANGUAGES}
                  setActive={() => setSkillsTab(SkillsTabs.LANGUAGES)}
                />
              </div>
            )}
          </div>
        </section>
      )}

      <section name="programs-certifications-learning">
        {/* Programs */}
        {hasPrograms && (
          <div
            id="ta-programs"
            className="colored-block blue-block programs-block"
          >
            <div className="content-block-container extra-padding ">
              <SectionHeader>
                <div>
                  <h2>Programs</h2>
                  <p>
                    This Teaching Artist's arts education program offerings are
                    listed below, along with the arts program types which best
                    define each respective program.
                  </p>
                </div>
              </SectionHeader>
              {programGroups.map((group, i) => (
                <div key={`program-group-${i}`}>
                  {group.map((program) => (
                    <ProgramBlock
                      key={`program-${program.id}`}
                      program={program}
                      isTeachingArtist={true}
                      programPhotosEnabled={programPhotoEnabled}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {hasCertifications && (
          <div className="content-block-container">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <CertificationIcon />
                </div>
                <span>{certifications.length}</span>
              </div>
              <h2>Licenses & Certifications</h2>
            </SectionHeader>
            <ul className="grid-container col-2">
              {certifications.map((certification) => (
                <li
                  key={`certification-${certification.id}`}
                  className="card flex-container"
                >
                  <div className="certification-container">
                    <div>
                      {certification.credentialName && (
                        <p className="certification-title">
                          {certification.credentialName}
                        </p>
                      )}
                      {certification.institutionName && (
                        <p className="certification-text">
                          {certification.institutionName}
                        </p>
                      )}
                      {certification.issueDate && (
                        <p className="certification-text">
                          {formatMonthYearDate(certification.issueDate)}
                        </p>
                      )}
                    </div>
                    <div>
                      {certification.linkCredential && (
                        <Link
                          to={certification.linkCredential}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="certification-link align-between"
                        >
                          Show Credential <VectorIcon />
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Professional Learning */}
        {hasProfessionalLearning && (
          <div className="content-block-container">
            <SectionHeader>
              <div className="image-block">
                <div className="section-header-icon">
                  <BookIcon />
                </div>
                <span>{professionalLearning.length}</span>
              </div>
              <h2>Professional Learning </h2>
            </SectionHeader>
            <table className="learning-table">
              <caption className="visually-hidden">
                Professional learning credentials and hours
              </caption>
              <colgroup>
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Credential</th>
                  <th scope="col">Hours</th>
                  <th scope="col">Institution</th>
                  <th scope="col">Date</th>
                </tr>
              </thead>
              <tbody>
                {professionalLearning.map((learning) => (
                  <tr key={`learning-${learning.id}`}>
                    <td>{learning.credentialName}</td>
                    <td>{learning.creditsHours} hs</td>
                    <td>{learning.institutionName}</td>
                    <td>{formatDate(learning.issueDate)}</td>
                  </tr>
                ))}
                <tr>
                  <th scope="row">
                    <b>Total</b>
                  </th>
                  <td>
                    <b>
                      {professionalLearning.reduce(
                        (acc, learning) => acc + Number(learning.creditsHours),
                        0
                      )}{' '}
                      hs
                    </b>
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>
    </LoadingContainer>
  )
}

ArtistContent.propTypes = propTypes
ArtistContent.defaultProps = defaultProps

export default ArtistContent
